import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import { buildArcStorageUrl } from "lib/utils/arcStorageUrl";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getClient } from "app/services/missionStorageApi";
import {
    deleteRuns,
    getRunLogs,
    getRunRecordingStatus,
    getRuns as getFleetRuns,
    MAX_PAGE_SIZE,
    refetchRun,
} from "lib/fleetMissionStorage/FleetMissionStorageClient";
import PresetDateRangeInput from "components/layout/inputs/PresetDateRangeInput";
import type { MissionOverviewRangeSettings } from "lib/fleetMissionStorage/missionOverviewRange";
import { resolveMissionOverviewDateRange } from "lib/fleetMissionStorage/missionOverviewRange";
import { subscribeSchedulePageChanges } from "lib/fleetMissionStorage/FleetScheduleClient";
import type { RootState } from "store";
import FunctionalButton from "components/layout/FunctionalButton";
import { formatLocalDateTime, parseBackendUtcDate } from "lib/utils/dateTimeDisplay";
import {
    formatMissionLogPayloadForDisplay,
    extractDetectionHits,
    parseMissionLogJson,
    DetectionHit,
} from "lib/utils/formatMissionLogDisplay";
import { filterDetectionHitsByCriteria } from "lib/mission/emailAlertCriteriaForm";
import { fetchLogEmailRules, type LogEmailRule } from "lib/mission/emailNotificationsApi";
import { mergeMissionLogEvents } from "lib/utils/mergeMissionLogEvents";
import FleetAttachmentImage from "components/logs/FleetAttachmentImage";
import FleetAttachmentVideo from "components/logs/FleetAttachmentVideo";
import FleetRunRecording from "components/logs/FleetRunRecording";
import type { FleetMissionRunRecording } from "lib/fleetMissionStorage/FleetMissionStorageClient";
import { ReactComponent as ArrowLeftIcon } from "assets/svgs/ArrowLeft.svg";
import { ReactComponent as DownloadIcon } from "assets/svgs/navbar/download.svg";
import { ReactComponent as SyncUploadingIcon } from "assets/svgs/sync/SyncUploading.svg";
import { ReactComponent as NotSyncIcon } from "assets/svgs/sync/NotSync.svg";
import { ReactComponent as PlayIcon } from "assets/svgs/navigation/mission/Play.svg";
import { ReactComponent as StopIcon } from "assets/svgs/navigation/mission/Stop.svg";
import { useOrgFleet } from "providers/OrgFleetProvider";
import {
    MissionRunRepository,
    reconcileEntities,
} from "lib/missionRuns/MissionRunRepository";

type AttachmentMeta = {
    id: string;
    fileName: string;
    mimeType: string;
    /** Nullable in the fleet read API's response. */
    size: number | null;
    /** Fleet only: import state of the copied bytes. Undefined in direct mode. */
    fetchStatus?: string;
    /** Fleet only: true when the imported bytes are on disk. */
    available?: boolean;
};

/**
 * Renders one attachment image. Direct mode builds a token-bearing ARC URL;
 * fleet mode fetches the imported bytes with a bearer header. Injected so the
 * markup below never needs to know which backend it is showing.
 */
type AttachmentImageComponent = React.ComponentType<{
    attachment: AttachmentMeta;
    className?: string;
    onClick?: () => void;
}>;

type AttachmentVideoComponent = React.ComponentType<{
    attachment: AttachmentMeta;
    className?: string;
}>;

type ClipJobSummary = {
    id: string;
    status: string;
    attachmentId: string | null;
};

type MissionLogEvent = {
    id: string;
    offsetMs: number;
    type: string;
    detail: string;
    payload: string;
    data: unknown;
    attachments: AttachmentMeta[];
    clipJobs: ClipJobSummary[];
};

type RunMeta = {
    id: string;
    sourceRunId: string;
    arcSerial?: string;
    missionId: string;
    name: string;
    status: string;
    startTime: string;
    startedAtMs: number;
    updatedAtMs: number;
    hasRecording: boolean;
};

type RawLogNode = {
    id: string;
    topic: string;
    background: boolean;
    notes: string;
    pose: unknown;
    data: unknown;
    createdAt: string;
    attachments: Array<{
        id: string;
        fileName: string;
        mimeType: string;
        size: number | null;
    }> | null;
    videoClipJobs: Array<{
        id: string;
        status: string;
        attachmentId: string | null;
    }> | null;
};

type TimelineScrollAnchor = {
    eventId: string;
    top: number;
};

const equalRunMeta = (previous: RunMeta, next: RunMeta): boolean => (
    previous.id === next.id
    && previous.sourceRunId === next.sourceRunId
    && previous.arcSerial === next.arcSerial
    && previous.missionId === next.missionId
    && previous.name === next.name
    && previous.status === next.status
    && previous.startTime === next.startTime
    && previous.startedAtMs === next.startedAtMs
    && previous.updatedAtMs === next.updatedAtMs
    && previous.hasRecording === next.hasRecording
);

const equalAttachment = (previous: AttachmentMeta, next: AttachmentMeta): boolean => (
    previous.id === next.id
    && previous.fileName === next.fileName
    && previous.mimeType === next.mimeType
    && previous.size === next.size
    && previous.fetchStatus === next.fetchStatus
    && previous.available === next.available
);

const equalClipJob = (previous: ClipJobSummary, next: ClipJobSummary): boolean => (
    previous.id === next.id
    && previous.status === next.status
    && previous.attachmentId === next.attachmentId
);

const equalRecording = (
    previous: FleetMissionRunRecording | null,
    next: FleetMissionRunRecording | null,
): boolean => {
    if (previous === next) return true;
    if (!previous || !next) return false;
    return previous.fileName === next.fileName
        && previous.mimeType === next.mimeType
        && previous.size === next.size
        && previous.fetchStatus === next.fetchStatus
        && previous.available === next.available;
};

const equalMissionLogEvent = (previous: MissionLogEvent, next: MissionLogEvent): boolean => (
    previous.id === next.id
    && previous.offsetMs === next.offsetMs
    && previous.type === next.type
    && previous.detail === next.detail
    && previous.payload === next.payload
    && previous.attachments.length === next.attachments.length
    && previous.attachments.every((attachment, index) => equalAttachment(attachment, next.attachments[index]))
    && previous.clipJobs.length === next.clipJobs.length
    && previous.clipJobs.every((job, index) => equalClipJob(job, next.clipJobs[index]))
);

const reconcileMissionLogEvent = (
    previous: MissionLogEvent,
    next: MissionLogEvent,
): MissionLogEvent => {
    if (equalMissionLogEvent(previous, next)) return previous;
    const attachments = reconcileEntities(
        previous.attachments,
        next.attachments,
        equalAttachment,
    );
    const clipJobs = reconcileEntities(
        previous.clipJobs,
        next.clipJobs,
        equalClipJob,
    );
    if (attachments === previous.attachments && clipJobs === previous.clipJobs) {
        return previous;
    }
    return { ...next, attachments, clipJobs };
};

const GET_RUNS_QUERY = `
query GetMissionRuns {
  missions {
    id
    name
    runs {
      id
      status
      createdAt
      updatedAt
      hasRecording
    }
  }
}
`;

const SOFT_DELETE_RUNS_MUTATION = `
mutation SoftDeleteRuns($ids: [String!]!) {
  softDeleteRuns(ids: $ids) {
    deletedRunIds
    alreadyDeletedRunIds
    missingRunIds
  }
}
`;

const GET_RUN_LOGS_QUERY = `
query GetRunLogs($runId: String!) {
  logs(runId: $runId) {
    id
    topic
    background
    notes
    pose
    data
    createdAt
    attachments {
      id
      fileName
      mimeType
      size
    }
    videoClipJobs {
      id
      status
      attachmentId
    }
  }
}
`;


const formatElapsed = (ms: number): string => {
    const clamped = Math.max(0, ms);
    const totalSeconds = Math.floor(clamped / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const mapTopicType = (topic: string): string => topic.replace(/^\//, "");

/**
 * Import state of a log row's attachments, for the fleet-only row indicator.
 *
 * In-progress wins over failed on a mixed row: the row is still settling, and
 * flagging a failure that may yet resolve is worse than showing nothing. A row
 * whose attachments are all stored returns "synced" and renders no icon.
 */
type AttachmentImportState = "none" | "uploading" | "failed" | "synced";

const IN_PROGRESS_FETCH_STATUSES = new Set(["waiting_source", "pending", "fetching"]);

const attachmentImportState = (
    attachments: AttachmentMeta[],
): AttachmentImportState => {
    if (attachments.length === 0) return "none";
    if (attachments.some((a) => a.fetchStatus && IN_PROGRESS_FETCH_STATUSES.has(a.fetchStatus))) {
        return "uploading";
    }
    if (attachments.some((a) => a.available === false)) return "failed";
    return "synced";
};

const DetectionPanel: React.FC<{
    hits: DetectionHit[];
    attachments: AttachmentMeta[];
    onImageClick: (att: AttachmentMeta) => void;
    AttachmentImage: AttachmentImageComponent;
    AttachmentVideo: AttachmentVideoComponent;
}> = ({ hits, attachments, onImageClick, AttachmentImage, AttachmentVideo }) => {
    return (
        <div className="bg-black80 px-3 py-2 space-y-2">
            <div className="flex flex-wrap gap-2">
                {hits.length === 0 ? (
                    <p className="text-h5 text-black40 italic">no target classes detected</p>
                ) : (
                    hits.map((h, i) => (
                        <span
                            key={i}
                            className="rounded-r5 border border-black70 bg-black90 px-2 py-0.5 text-h5 text-white"
                        >
                            {h.class_id} {Math.round(h.score * 100)}%
                        </span>
                    ))
                )}
            </div>
            {attachments.filter((a) => a.mimeType.startsWith("image/")).length > 0 && (
                <div className="flex gap-2 overflow-x-auto">
                    {attachments.filter((a) => a.mimeType.startsWith("image/")).map((att) => (
                        <button
                            key={att.id}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onImageClick(att); }}
                            className="h-32 w-48 shrink-0 overflow-hidden rounded-r5 border border-black70 hover:border-primary2"
                        >
                            <AttachmentImage
                                attachment={att}
                                className="h-full w-full object-cover"
                            />
                        </button>
                    ))}
                </div>
            )}
            {attachments.filter((a) => a.mimeType.startsWith("video/")).map((att) => (
                <AttachmentVideo
                    key={att.id}
                    attachment={att}
                    className="h-48 w-full max-w-xl rounded-r5"
                />
            ))}
        </div>
    );
};

const isDetectionResultData = (data: unknown): boolean => {
    const parsed = parseMissionLogJson(data);
    const wrapper = parsed?.detections;
    return Boolean(
        wrapper
        && typeof wrapper === "object"
        && !Array.isArray(wrapper)
        && Array.isArray((wrapper as Record<string, unknown>).detections),
    );
};

const SHOW_MAP_REPLAY = false;

const logFleetFeature = (
    tag: "FMS-RUN-READ" | "FMS-MEDIA-RETRY" | "FMS-RUN-DELETE",
    event: string,
    fields: Record<string, string | number | boolean | null>,
) => {
    // Keep browser diagnostics grep-friendly without logging tokens or log payloads.
    console.info(`[${tag}] ${event}`, fields);
};

type ControlLogsPageProps = {
    fleetMode?: boolean;
    rangeSettings?: MissionOverviewRangeSettings;
    onRangeSettingsChange?: (value: MissionOverviewRangeSettings) => void;
};

const ControlLogsPage: React.FC<ControlLogsPageProps> = ({
    fleetMode = false,
    rangeSettings,
    onRangeSettingsChange,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { runId: urlRunId } = useParams<{ runId?: string }>();
    const isSecure = useIsHttpSecure();
    const { arcId, arcProxyHost } = useSelector((state: RootState) => state.arcProxy);
    const fleetContext = useOrgFleet();
    const fleetProxyHost = fleetContext?.proxyHostname ?? null;
    const fleetDateRange = rangeSettings
        ? resolveMissionOverviewDateRange(rangeSettings)
        : null;
    const fleetCustomRangeInvalid = Boolean(
        fleetMode && rangeSettings?.selection === "custom" && fleetDateRange === null,
    );

    const arcStorageUrl = (path: string, runArcSerial?: string): string =>
        buildArcStorageUrl(path, {
            isSecure,
            arcId: fleetMode ? runArcSerial : arcId,
            arcProxyHost: fleetMode ? fleetProxyHost : arcProxyHost,
        });

    const [runs, setRuns] = useState<RunMeta[]>([]);
    const [isRunsLoading, setIsRunsLoading] = useState(true);
    const [runsError, setRunsError] = useState<string>("");
    const [runsRefreshToken, setRunsRefreshToken] = useState(0);
    const [timelineRefreshToken, setTimelineRefreshToken] = useState(0);
    const [isRunDeleteMode, setIsRunDeleteMode] = useState(false);
    const [selectedRunIdsForDelete, setSelectedRunIdsForDelete] = useState<Set<string>>(new Set());
    const [isRunMenuOpen, setIsRunMenuOpen] = useState(false);
    const runMenuRef = useRef<HTMLDivElement>(null);
    const [isDeletingRuns, setIsDeletingRuns] = useState(false);
    const [runDeleteError, setRunDeleteError] = useState("");

    const [selectedRunId, setSelectedRunId] = useState<string>("");
    const [selectedRunEvents, setSelectedRunEvents] = useState<MissionLogEvent[]>([]);
    const [selectedRunDurationMs, setSelectedRunDurationMs] = useState(1);
    const [isEventsLoading, setIsEventsLoading] = useState(false);
    const [eventsError, setEventsError] = useState<string>("");
    const [selectedRecording, setSelectedRecording] = useState<FleetMissionRunRecording | null>(null);
    const [isRecordingMetadataLoading, setIsRecordingMetadataLoading] = useState(false);
    const [isTimelineComplete, setIsTimelineComplete] = useState(!fleetMode);
    const [isRefetchingRecording, setIsRefetchingRecording] = useState(false);
    const [recordingRefetchMessage, setRecordingRefetchMessage] = useState("");
    const [recordingRefreshToken, setRecordingRefreshToken] = useState(0);

    const runRepository = useRef(
        new MissionRunRepository<RunMeta, MissionLogEvent>(),
    );
    const videoRef = useRef<HTMLVideoElement>(null);
    const loadedRunIdRef = useRef<string | null>(null);
    const autoExpandedRef = useRef<Set<string>>(new Set());
    const eventRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const lastScrolledEventRef = useRef<string | null>(null);
    const timelineScrollRef = useRef<HTMLDivElement>(null);
    const timelineScrollAnchorRef = useRef<TimelineScrollAnchor | null>(null);

    const [activeTab, setActiveTab] = useState<"Mission Logs" | "Photos & Vids">("Mission Logs");
    const [isPlaying, setIsPlaying] = useState(false);
    const [playheadMs, setPlayheadMs] = useState(0);
    const [expandedEventIds, setExpandedEventIds] = useState<Set<string>>(new Set());
    const [lightboxAttachment, setLightboxAttachment] = useState<AttachmentMeta | null>(null);
    const [emailRules, setEmailRules] = useState<LogEmailRule[]>([]);

    const requestedFleetRunId = fleetMode
        ? (() => {
            const params = new URLSearchParams(location.search);
            const arcSerial = params.get("arc");
            const sourceRunId = params.get("runId");
            return arcSerial && sourceRunId ? `${arcSerial}:${sourceRunId}` : null;
        })()
        : null;

    const isMissionLogsTab = activeTab === "Mission Logs";

    const selectedRun = useMemo(
        () => runs.find((r) => r.id === selectedRunId),
        [runs, selectedRunId]
    );

    const handleRunSelect = (id: string) => {
        if (fleetMode) {
            setSelectedRunId(id);
            return;
        }
        if (urlRunId) {
            navigate(`../${id}`, { relative: "path" });
        } else {
            navigate(id);
        }
    };

    const enterRunDeleteMode = () => {
        setIsRunDeleteMode(true);
        setSelectedRunIdsForDelete(new Set());
        setIsRunMenuOpen(false);
        setRunDeleteError("");
    };

    const cancelRunDeleteMode = () => {
        if (isDeletingRuns) return;
        setIsRunDeleteMode(false);
        setSelectedRunIdsForDelete(new Set());
        setIsRunMenuOpen(false);
        setRunDeleteError("");
    };

    const toggleRunForDelete = (runId: string) => {
        setSelectedRunIdsForDelete((current) => {
            const next = new Set(current);
            if (next.has(runId)) next.delete(runId);
            else next.add(runId);
            return next;
        });
    };

    const handleDeleteRuns = async () => {
        if (selectedRunIdsForDelete.size === 0 || isDeletingRuns) return;
        if (fleetMode && !fleetProxyHost) return;
        const selectedIds = new Set(selectedRunIdsForDelete);
        const selectedRuns = runs.filter((run) => selectedIds.has(run.id));
        if (selectedRuns.length === 0) {
            cancelRunDeleteMode();
            return;
        }
        const confirmText = fleetMode
            ? "Delete the selected mission logs and their imported media permanently?"
            : "Delete the selected mission logs?";
        if (!window.confirm(confirmText)) return;
        setIsDeletingRuns(true);
        setRunDeleteError("");
        try {
            let goneIds: Set<string>;
            if (fleetMode) {
                const response = await deleteRuns(
                    fleetProxyHost!,
                    selectedRuns.map((run) => ({
                        sourceArcSerial: run.arcSerial ?? "",
                        sourceRunId: run.sourceRunId,
                    })),
                );
                if (!response.accepted) throw new Error("Delete request was not accepted");
                goneIds = selectedIds;
                logFleetFeature("FMS-RUN-DELETE", "delete.end", {
                    outcome: "accepted",
                    requested_runs: selectedRuns.length,
                    deleted_runs: response.deletedRunCount,
                });
            } else {
                const response = await getClient(arcProxyHost, arcId).request<{
                    softDeleteRuns: {
                        deletedRunIds: string[];
                        alreadyDeletedRunIds: string[];
                        missingRunIds: string[];
                    };
                }>(SOFT_DELETE_RUNS_MUTATION, { ids: selectedRuns.map((run) => run.id) });
                const result = response.softDeleteRuns;
                goneIds = new Set([
                    ...result.deletedRunIds,
                    ...result.alreadyDeletedRunIds,
                    ...result.missingRunIds,
                ]);
            }
            goneIds.forEach((id) => runRepository.current.deleteRun(id));
            setRuns((current) => current.filter((run) => !goneIds.has(run.id)));
            if (goneIds.has(selectedRunId)) {
                setSelectedRunId("");
                setSelectedRunEvents([]);
                setSelectedRecording(null);
                setIsTimelineComplete(false);
                setIsPlaying(false);
                if (!fleetMode && urlRunId) navigate("..", { relative: "path" });
            }
            setSelectedRunIdsForDelete(new Set());
            setIsRunDeleteMode(false);
            setIsRunMenuOpen(false);
            setRunsRefreshToken((value) => value + 1);
        } catch (error) {
            const message = error instanceof Error ? error.message : "";
            setRunDeleteError(
                message.includes("run_active")
                    ? "A selected log is still running and cannot be deleted."
                    : "Could not delete the selected mission logs.",
            );
            if (fleetMode) {
                logFleetFeature("FMS-RUN-DELETE", "delete.end", {
                    outcome: error instanceof Error ? error.name : "error",
                    requested_runs: selectedRuns.length,
                    deleted_runs: null,
                });
            }
        } finally {
            setIsDeletingRuns(false);
        }
    };

    useEffect(() => {
        if (!isRunMenuOpen) return;
        const onPointerDown = (event: MouseEvent) => {
            if (!runMenuRef.current?.contains(event.target as Node)) setIsRunMenuOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setIsRunMenuOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isRunMenuOpen]);

    // Sync URL param → selected run when navigating via browser back/forward
    useEffect(() => {
        if (fleetMode) return;
        if (!urlRunId || runs.length === 0) return;
        if (runs.some(r => r.id === urlRunId)) {
            setSelectedRunId(urlRunId);
        }
    }, [urlRunId, runs]);

    // Email rules: used to filter detection-panel chips (or show all when match_enabled_classes).
    useEffect(() => {
        if (fleetMode) return;
        let cancelled = false;
        void fetchLogEmailRules(arcProxyHost, arcId)
            .then((rules) => {
                if (!cancelled) setEmailRules(rules);
            })
            .catch(() => {
                if (!cancelled) setEmailRules([]);
            });
        return () => {
            cancelled = true;
        };
    }, [fleetMode, arcProxyHost, arcId]);

    const topicRuleCriteria = useMemo(() => {
        const map: Record<string, Record<string, unknown>> = {};
        for (const rule of emailRules) {
            if (!rule.enabled) continue;
            const criteria = (rule.criteria ?? {}) as Record<string, unknown>;
            const topic =
                typeof criteria.topic === "string" ? criteria.topic.replace(/^\//, "") : "";
            if (!topic || map[topic]) continue;
            map[topic] = criteria;
        }
        return map;
    }, [emailRules]);

    const toggleExpanded = (id: string) =>
        setExpandedEventIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const requestedRunPending = Boolean(
        fleetMode
        && requestedFleetRunId
        && !isRunsLoading
        && !runsError
        && !runs.some((run) => run.id === requestedFleetRunId)
    );

    // Load runs on mount and whenever the mission importer reports a run change.
    useEffect(() => {
        let isCancelled = false;
        const fetchRuns = async () => {
            setIsRunsLoading(true);
            setRunsError("");
            try {
                let mapped: RunMeta[];
                if (fleetMode) {
                    if (!fleetProxyHost) throw new Error("No fleet proxy configured");
                    const response = fleetCustomRangeInvalid
                        ? []
                        : await getFleetRuns(
                            fleetProxyHost,
                            MAX_PAGE_SIZE,
                            0,
                            fleetDateRange ?? undefined,
                        );
                    mapped = response.map((run) => ({
                        id: `${run.sourceArcSerial}:${run.sourceRunId}`,
                        sourceRunId: run.sourceRunId,
                        arcSerial: run.sourceArcSerial,
                        missionId: run.sourceMissionId,
                        name: run.missionName,
                        status: run.status,
                        startTime: formatLocalDateTime(run.createdAt),
                        startedAtMs: parseBackendUtcDate(run.createdAt).getTime(),
                        updatedAtMs: parseBackendUtcDate(run.updatedAt).getTime(),
                        hasRecording: false,
                    }));
                } else {
                    const response = await getClient(arcProxyHost, arcId).request<{
                        missions: Array<{
                            id: string;
                            name: string;
                            runs: Array<{
                                id: string;
                                status: string;
                                createdAt: string;
                                updatedAt: string;
                                hasRecording: boolean;
                            }>;
                        }>;
                    }>(GET_RUNS_QUERY);

                    mapped = response.missions.flatMap((mission) =>
                        mission.runs.map((run) => ({
                            id: run.id,
                            sourceRunId: run.id,
                            missionId: mission.id,
                            name: mission.name,
                            status: run.status,
                            startTime: formatLocalDateTime(run.createdAt),
                            startedAtMs: parseBackendUtcDate(run.createdAt).getTime(),
                            updatedAtMs: parseBackendUtcDate(run.updatedAt).getTime(),
                            hasRecording: run.hasRecording,
                        }))
                    );
                }
                mapped.sort((a, b) => b.startedAtMs - a.startedAtMs);

                if (!isCancelled) {
                    const reconciledRuns = runRepository.current.reconcileRuns(mapped, equalRunMeta);
                    setRuns((previous) => previous === reconciledRuns ? previous : reconciledRuns);
                    const requestedRunId = requestedFleetRunId ?? urlRunId;
                    setSelectedRunId((currentSelectedRunId) => {
                        if (requestedRunId && reconciledRuns.some((run) => run.id === requestedRunId)) {
                            return requestedRunId;
                        }
                        if (requestedRunId) {
                            // A linked Fleet run may not have reached the read model
                            // yet. Keep the detail pane empty instead of opening a
                            // different, newer run while the importer catches up.
                            return "";
                        }
                        // Refreshes prepend newer runs. Keep the user's current
                        // selection while it still exists; only choose the newest
                        // run for the initial empty state or after its run is gone.
                        if (currentSelectedRunId && reconciledRuns.some((run) => run.id === currentSelectedRunId)) {
                            return currentSelectedRunId;
                        }
                        return reconciledRuns[0]?.id ?? "";
                    });
                }
            } catch {
                if (!isCancelled) {
                    setRunsError("Failed to load runs.");
                }
            } finally {
                if (!isCancelled) {
                    setIsRunsLoading(false);
                }
            }
        };
        fetchRuns();
        return () => { isCancelled = true; };
    }, [
        arcId,
        arcProxyHost,
        fleetMode,
        fleetProxyHost,
        location.search,
        requestedFleetRunId,
        runsRefreshToken,
        urlRunId,
        fleetDateRange?.startDate,
        fleetDateRange?.endDate,
        fleetCustomRangeInvalid,
    ]);

    useEffect(() => {
        if (!fleetMode || !fleetProxyHost) return undefined;
        return subscribeSchedulePageChanges(
            fleetProxyHost,
            null,
            (entities) => {
                if (!entities.includes("runs")) return;
                // The coarse hint covers run metadata, logs, and media import
                // state. Retain the current snapshot while the next complete
                // detail snapshot is staged and reconciled.
                runRepository.current.markTimelineStale();
                setTimelineRefreshToken((value) => value + 1);
                setRunsRefreshToken((value) => value + 1);
            },
            (error) => console.warn("Fleet run list subscription disconnected", error),
        );
    }, [fleetMode, fleetProxyHost]);

    // Load logs when a run is selected or its repository entry is stale. A
    // refresh is staged off-screen and committed atomically, so the current
    // timeline never flashes a partial first page.
    useEffect(() => {
        if (!selectedRunId) return;

        const isRunRefresh = loadedRunIdRef.current === selectedRunId;
        const cached = runRepository.current.getTimeline(selectedRunId);
        const shouldRefresh = !cached || runRepository.current.isTimelineStale(selectedRunId);

        if (!isRunRefresh && !cached) {
            setIsPlaying(false);
            setPlayheadMs(0);
            setExpandedEventIds(new Set());
            autoExpandedRef.current = new Set();
            lastScrolledEventRef.current = null;
            setSelectedRunEvents([]);
        }

        if (cached) {
            loadedRunIdRef.current = selectedRunId;
            setSelectedRunEvents(cached.events);
            setSelectedRunDurationMs(cached.durationMs);
            setIsTimelineComplete(true);
            setIsEventsLoading(false);
        }

        if (!shouldRefresh) return;

        const run = selectedRun;

        let isCancelled = false;
        const controller = new AbortController();
        const fetchEvents = async () => {
            setIsEventsLoading(!cached);
            if (!cached) setIsTimelineComplete(false);
            setEventsError("");
            logFleetFeature("FMS-RUN-READ", "timeline.start", {
                run_id: selectedRunId,
                mode: fleetMode ? "fleet" : "direct",
                refresh: isRunRefresh,
            });

            try {
                const startMs = run?.startedAtMs ?? 0;
                const updatedAtMs = run?.updatedAtMs ?? startMs;
                const logsById = new Map<string, RawLogNode>();

                // Pages are staged here rather than published as they arrive.
                // Publishing partial pages was the source of row unmounts and
                // media reloads during background refreshes.
                const stagePage = (pageLogs: RawLogNode[]) => {
                    for (const log of pageLogs) logsById.set(log.id, log);
                };

                const buildTimeline = (): { events: MissionLogEvent[]; durationMs: number } => {
                    const events = [...logsById.values()]
                        .filter((log) => !log.background)
                        .sort((a, b) => {
                            const timeDelta = parseBackendUtcDate(a.createdAt).getTime()
                                - parseBackendUtcDate(b.createdAt).getTime();
                            return timeDelta || a.id.localeCompare(b.id);
                        })
                        .map<MissionLogEvent>((log) => {
                            const eventMs = parseBackendUtcDate(log.createdAt).getTime();
                            const attachmentNodes = log.attachments ?? [];
                            const clipJobNodes = log.videoClipJobs ?? [];
                            return {
                                id: log.id,
                                offsetMs: Math.max(0, eventMs - startMs),
                                type: mapTopicType(log.topic),
                                detail: log.notes,
                                data: log.data,
                                payload: formatMissionLogPayloadForDisplay(log.data, log.pose),
                                attachments: attachmentNodes,
                                clipJobs: clipJobNodes.map((j) => ({
                                    id: j.id,
                                    status: j.status,
                                    attachmentId: j.attachmentId,
                                })),
                            };
                        });
                    const eventsEndMs = events.length > 0
                        ? startMs + events[events.length - 1].offsetMs
                        : startMs;
                    const durationMs = Math.max(1, Math.max(updatedAtMs, eventsEndMs) - startMs);
                    return { events, durationMs };
                };

                let cursor: string | null = null;
                const seenCursors = new Set<string>();
                do {
                    let pageLogs: RawLogNode[];
                    if (fleetMode) {
                        if (!fleetProxyHost || !run?.arcSerial) {
                            throw new Error("Fleet run is missing its ARC identity");
                        }
                        logFleetFeature("FMS-RUN-READ", "timeline.page.start", {
                            run_id: selectedRunId,
                            cursor_present: Boolean(cursor),
                        });
                        const response = await getRunLogs(
                            fleetProxyHost,
                            run.arcSerial,
                            run.sourceRunId,
                            { limit: 500, cursor, signal: controller.signal },
                        );
                        if (!response) throw new Error("Run not found");
                        pageLogs = response.logs;
                        const nextCursor = response.nextCursor;
                        logFleetFeature("FMS-RUN-READ", "timeline.page.end", {
                            run_id: selectedRunId,
                            page_logs: pageLogs.length,
                            next_page: Boolean(nextCursor),
                            cursor_present: Boolean(cursor),
                        });
                        stagePage(pageLogs);
                        if (nextCursor && seenCursors.has(nextCursor)) {
                            throw new Error("Fleet log cursor repeated");
                        }
                        if (nextCursor) seenCursors.add(nextCursor);
                        cursor = nextCursor;
                    } else {
                        const response = await getClient(arcProxyHost, arcId).request<{
                            logs: RawLogNode[];
                        }>(GET_RUN_LOGS_QUERY, { runId: selectedRunId });
                        pageLogs = response.logs;
                        stagePage(pageLogs);
                        cursor = null;
                    }
                } while (cursor && !isCancelled);

                if (!isCancelled) {
                    const latestTimeline = runRepository.current.reconcileTimeline(
                        selectedRunId,
                        buildTimeline(),
                        equalMissionLogEvent,
                        reconcileMissionLogEvent,
                    );
                    if (cached) {
                        const scrollContainer = timelineScrollRef.current;
                        if (scrollContainer) {
                            const containerTop = scrollContainer.getBoundingClientRect().top;
                            const anchor = [...eventRowRefs.current.entries()]
                                .map(([eventId, element]) => ({
                                    eventId,
                                    top: element.getBoundingClientRect().top - containerTop,
                                }))
                                .find(({ top }) => top >= 0);
                            if (anchor) timelineScrollAnchorRef.current = anchor;
                        }
                    }
                    loadedRunIdRef.current = selectedRunId;
                    setSelectedRunEvents(latestTimeline.events);
                    setSelectedRunDurationMs(latestTimeline.durationMs);
                    setIsTimelineComplete(true);
                    setIsEventsLoading(false);
                    setExpandedEventIds((previous) => {
                        const eventIds = new Set(latestTimeline.events.map((event) => event.id));
                        return new Set([...previous].filter((id) => eventIds.has(id)));
                    });
                    logFleetFeature("FMS-RUN-READ", "timeline.end", {
                        run_id: selectedRunId,
                        events: latestTimeline.events.length,
                        duration_ms: latestTimeline.durationMs,
                        outcome: "complete",
                    });
                }
            } catch (error) {
                if (!isCancelled && !controller.signal.aborted) {
                    setEventsError("Failed to load events for this run.");
                    logFleetFeature("FMS-RUN-READ", "timeline.end", {
                        run_id: selectedRunId,
                        events: selectedRunEvents.length,
                        outcome: error instanceof Error ? error.name : "error",
                    });
                }
            } finally {
                if (!isCancelled) {
                    setIsEventsLoading(false);
                }
            }
        };
        fetchEvents();
        return () => {
            isCancelled = true;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        arcId,
        arcProxyHost,
        fleetMode,
        fleetProxyHost,
        selectedRunId,
        selectedRun?.arcSerial,
        selectedRun?.sourceRunId,
        selectedRun?.startedAtMs,
        selectedRun?.updatedAtMs,
        timelineRefreshToken,
    ]);

    // Recording import state is independent from the log-event cache. The
    // importer can finish copying bytes after the run details were cached, so
    // poll this small response until the recording reaches a terminal state.
    useEffect(() => {
        if (!fleetMode || !fleetProxyHost || !selectedRun?.arcSerial) {
            setSelectedRecording(null);
            setIsRecordingMetadataLoading(false);
            return undefined;
        }
        const compositeKey = `${selectedRun.arcSerial}:${selectedRun.sourceRunId}`;
        let cancelled = false;
        let timer: number | undefined;
        const controller = new AbortController();
        const cachedRecording = runRepository.current.hasRecording(selectedRun.id)
            ? runRepository.current.getRecording<FleetMissionRunRecording>(selectedRun.id) ?? null
            : undefined;
        const fetchRecording = async () => {
            try {
                logFleetFeature("FMS-MEDIA-RETRY", "recording_status.start", {
                    run_id: selectedRun.sourceRunId,
                    serial: selectedRun.arcSerial,
                });
                const response = await getRunRecordingStatus(
                    fleetProxyHost,
                    selectedRun.arcSerial as string,
                    selectedRun.sourceRunId,
                    controller.signal,
                );
                if (cancelled || compositeKey !== `${selectedRun.arcSerial}:${selectedRun.sourceRunId}`) return;
                const recording = response?.recording ?? null;
                logFleetFeature("FMS-MEDIA-RETRY", "recording_status.end", {
                    run_id: selectedRun.sourceRunId,
                    serial: selectedRun.arcSerial,
                    status: recording?.fetchStatus ?? "missing",
                    available: Boolean(response?.hasRecording),
                });
                const reconciledRecording = runRepository.current.reconcileRecording(
                    selectedRun.id,
                    recording,
                    equalRecording,
                );
                setSelectedRecording((previous) => previous === reconciledRecording
                    ? previous
                    : reconciledRecording);
                setIsRecordingMetadataLoading(false);
                if (recording && ["waiting_source", "pending", "fetching", "skipped"].includes(recording.fetchStatus)) {
                    timer = window.setTimeout(fetchRecording, 5000);
                }
            } catch (error) {
                if (!cancelled && !controller.signal.aborted) {
                    setIsRecordingMetadataLoading(false);
                    logFleetFeature("FMS-MEDIA-RETRY", "recording_status.end", {
                        run_id: selectedRun.sourceRunId,
                        serial: selectedRun.arcSerial,
                        outcome: error instanceof Error ? error.name : "error",
                    });
                }
            }
        };
        if (cachedRecording !== undefined) {
            setSelectedRecording((previous) => equalRecording(previous, cachedRecording)
                ? previous
                : cachedRecording);
            setIsRecordingMetadataLoading(false);
        } else {
            setSelectedRecording(null);
            setIsRecordingMetadataLoading(true);
        }
        void fetchRecording();
        return () => {
            cancelled = true;
            controller.abort();
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, [
        fleetMode,
        fleetProxyHost,
        recordingRefreshToken,
        selectedRun?.arcSerial,
        selectedRun?.id,
        selectedRun?.sourceRunId,
    ]);

    const handleRecordingRefetch = async () => {
        if (
            !fleetProxyHost
            || !selectedRun?.arcSerial
            || !selectedRun.sourceRunId
            || isRefetchingRecording
        ) return;
        setIsRefetchingRecording(true);
        setRecordingRefetchMessage("");
        logFleetFeature("FMS-MEDIA-RETRY", "recording_refetch.start", {
            run_id: selectedRun.sourceRunId,
            serial: selectedRun.arcSerial,
        });
        try {
            const response = await refetchRun(
                fleetProxyHost,
                selectedRun.arcSerial,
                selectedRun.sourceRunId,
            );
            if (!response) {
                setRecordingRefetchMessage("The run is no longer visible or available.");
                logFleetFeature("FMS-MEDIA-RETRY", "recording_refetch.end", {
                    run_id: selectedRun.sourceRunId,
                    serial: selectedRun.arcSerial,
                    outcome: "not_found",
                });
                return;
            }
            setRecordingRefetchMessage(
                response.resetMediaRows > 0
                    ? "Recording import retry requested."
                    : "Recording import is already in progress or complete.",
            );
            setSelectedRecording((current) => {
                if (!current) return current;
                const next = { ...current, fetchStatus: response.status, available: false };
                runRepository.current.reconcileRecording(selectedRun.id, next, equalRecording);
                return next;
            });
            setRecordingRefreshToken((value) => value + 1);
            logFleetFeature("FMS-MEDIA-RETRY", "recording_refetch.end", {
                run_id: selectedRun.sourceRunId,
                serial: selectedRun.arcSerial,
                outcome: "accepted",
                status: response.status,
                reset_rows: response.resetMediaRows,
            });
        } catch (error) {
            setRecordingRefetchMessage("Could not retry the recording import.");
            logFleetFeature("FMS-MEDIA-RETRY", "recording_refetch.end", {
                run_id: selectedRun.sourceRunId,
                serial: selectedRun.arcSerial,
                outcome: error instanceof Error ? error.name : "error",
            });
        } finally {
            setIsRefetchingRecording(false);
        }
    };

    /**
     * Fleet attachments come from the imported blob store, not from the ARC —
     * that is the point of importing them, and the robot may be offline. The
     * fleet route also authenticates the organization token on a header, which
     * an <img src> cannot send, so those bytes are fetched into a blob.
     *
     * Direct mode is unchanged: buildArcStorageUrl's ?token= is accepted by the
     * ARC gateway's forward-auth.
     */
    const AttachmentImage: AttachmentImageComponent = useMemo(() => {
        if (fleetMode) {
            const serial = selectedRun?.arcSerial ?? null;
            return ({ attachment, className, onClick }) => (
                <FleetAttachmentImage
                    proxyHostname={fleetProxyHost}
                    sourceArcSerial={serial}
                    attachment={attachment}
                    className={className}
                    onClick={onClick}
                />
            );
        }
        return ({ attachment, className, onClick }) => (
            <img
                src={buildArcStorageUrl(`/attachments/${attachment.id}/file`, {
                    isSecure,
                    arcId,
                    arcProxyHost,
                })}
                alt={attachment.fileName}
                className={className}
                onClick={onClick}
            />
        );
    }, [fleetMode, fleetProxyHost, selectedRun?.arcSerial, isSecure, arcId, arcProxyHost]);

    const AttachmentVideo: AttachmentVideoComponent = useMemo(() => {
        if (fleetMode) {
            const serial = selectedRun?.arcSerial ?? null;
            return ({ attachment, className }) => (
                <FleetAttachmentVideo
                    proxyHostname={fleetProxyHost}
                    sourceArcSerial={serial}
                    attachment={attachment}
                    className={className}
                />
            );
        }
        return ({ attachment, className }) => {
            const url = buildArcStorageUrl(`/attachments/${attachment.id}/file`, {
                isSecure,
                arcId,
                arcProxyHost,
            });
            return (
                <div className={`relative overflow-hidden bg-black90 ${className ?? ""}`}>
                    <video
                        src={url}
                        controls
                        playsInline
                        preload="metadata"
                        className="h-full w-full object-contain"
                        aria-label={attachment.fileName}
                    />
                    <a
                        href={url}
                        download={attachment.fileName}
                        className="absolute bottom-2 right-2 z-10 rounded-r5 bg-black90/80 p-1.5 text-white hover:bg-primary2"
                        aria-label={`Download ${attachment.fileName}`}
                        title="Download clip"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <DownloadIcon className="h-4 w-4" />
                    </a>
                </div>
            );
        };
    }, [fleetMode, fleetProxyHost, selectedRun?.arcSerial, isSecure, arcId, arcProxyHost]);

    const allImages = useMemo(
        () => selectedRunEvents.flatMap((e) => e.attachments.filter((a) => a.mimeType.startsWith("image/"))),
        [selectedRunEvents]
    );

    useLayoutEffect(() => {
        const anchor = timelineScrollAnchorRef.current;
        const scrollContainer = timelineScrollRef.current;
        if (!anchor || !scrollContainer) return;
        const element = eventRowRefs.current.get(anchor.eventId);
        if (element) {
            const containerTop = scrollContainer.getBoundingClientRect().top;
            const nextTop = element.getBoundingClientRect().top - containerTop;
            scrollContainer.scrollTop += nextTop - anchor.top;
        }
        timelineScrollAnchorRef.current = null;
    }, [selectedRunEvents]);

    const visibleMissionLogEvents = useMemo(
        () => mergeMissionLogEvents(selectedRunEvents.filter((event) => {
            const isDetectionEvent = isDetectionResultData(event.data);
            if (!isDetectionEvent) return true;

            return extractDetectionHits(event.data).length > 0;
        })),
        [selectedRunEvents],
    );

    const progressRatio = selectedRunDurationMs > 0 ? playheadMs / selectedRunDurationMs : 0;

    useEffect(() => {
        if (!isPlaying) return undefined;

        const interval = window.setInterval(() => {
            setPlayheadMs((prev) => {
                const next = prev + 100;
                if (next >= selectedRunDurationMs) {
                    setIsPlaying(false);
                    return selectedRunDurationMs;
                }
                return next;
            });
        }, 100);

        return () => window.clearInterval(interval);
    }, [isPlaying, selectedRunDurationMs]);

    // Auto-expand expandable events as playhead passes them; never auto-collapse
    useEffect(() => {
        const newIds: string[] = [];
        for (const event of visibleMissionLogEvents) {
            if (event.offsetMs <= playheadMs) {
                const isDetectionTopic = isDetectionResultData(event.data);
                const isExpandable = isDetectionTopic || event.attachments.length > 0;
                if (isExpandable && !autoExpandedRef.current.has(event.id)) {
                    autoExpandedRef.current.add(event.id);
                    newIds.push(event.id);
                }
            }
        }
        if (newIds.length > 0) {
            setExpandedEventIds((prev) => {
                const next = new Set(prev);
                for (const id of newIds) next.add(id);
                return next;
            });
        }
    }, [playheadMs, visibleMissionLogEvents]);

    // Scroll the most recently activated event into view as the playhead advances
    useEffect(() => {
        let lastActive: MissionLogEvent | undefined;
        for (const event of visibleMissionLogEvents) {
            if (event.offsetMs <= playheadMs) lastActive = event;
            else break;
        }
        if (lastActive && lastActive.id !== lastScrolledEventRef.current) {
            lastScrolledEventRef.current = lastActive.id;
            eventRowRefs.current.get(lastActive.id)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [playheadMs, visibleMissionLogEvents]);

    return (
        <div className={`${fleetMode ? "page-with-top-bar" : "h-full"} flex min-h-0 w-full flex-col bg-black90`}>
            <div className="flex shrink-0 items-center justify-between border-b border-black80 px-4 py-3">
                <h2 className="text-h3 font-semibold text-white">Logs</h2>
                <div className="ml-auto flex items-center gap-2">
                    {fleetMode && rangeSettings && onRangeSettingsChange && (
                        <PresetDateRangeInput
                            value={rangeSettings}
                            onChange={onRangeSettingsChange}
                        />
                    )}
                    <FunctionalButton
                        aria-label={fleetMode ? "Back to dashboard" : "Back"}
                        icon={<ArrowLeftIcon className="h-4 w-4 fill-white" />}
                        className="h-9 w-9 shrink-0 rounded-r5 border border-primary2 bg-primary2 hover:bg-primary1"
                        onClick={() => navigate(fleetMode ? "/fleet/dashboard" : "..")}
                    />
                </div>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden p-4">
                <div className="flex h-full min-h-0 gap-4">
                    <aside className="flex w-64 min-w-[14rem] flex-col rounded-r10 border border-black80 bg-black100">
                        <div className="flex shrink-0 items-stretch border-b border-black80">
                            {(["Mission Logs", "Photos & Vids"] as const).map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    className={`flex-1 border-b px-3 py-2 text-h5 ${activeTab === tab
                                        ? "border-primary2 text-white"
                                        : "border-transparent text-black40 hover:text-white"
                                        }`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                            {activeTab === "Mission Logs" && (
                                <div ref={runMenuRef} className="relative flex items-center pr-1">
                                    <button
                                        type="button"
                                        aria-label="Log options"
                                        aria-haspopup="menu"
                                        aria-expanded={isRunMenuOpen}
                                        className="flex h-7 w-7 items-center justify-center rounded text-lg leading-none text-black40 hover:bg-black70 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={() => setIsRunMenuOpen((open) => !open)}
                                        disabled={isRunDeleteMode}
                                    >
                                        ⋮
                                    </button>
                                    {isRunMenuOpen && (
                                        <div
                                            role="menu"
                                            className="absolute right-0 top-full z-20 min-w-32 rounded-r5 border border-black60 bg-black100 p-1 shadow-lg"
                                        >
                                            <button
                                                type="button"
                                                role="menuitem"
                                                className="w-full rounded px-2 py-1 text-left text-h5 text-white hover:bg-black70"
                                                onClick={enterRunDeleteMode}
                                            >
                                                Delete log
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                            {activeTab === "Mission Logs" && isRunDeleteMode && (
                                <div className="shrink-0 border-b border-black80 p-3">
                                <div className="rounded-r5 border border-primary2/60 bg-black90 p-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-h5 font-semibold text-white">
                                            Select log to delete
                                        </span>
                                        <span className="text-h5 text-black40">
                                            {selectedRunIdsForDelete.size} selected
                                        </span>
                                        <button
                                            type="button"
                                            className="text-h5 text-black40 underline hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                            onClick={cancelRunDeleteMode}
                                            disabled={isDeletingRuns}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        className="mt-2 text-h5 text-warning underline hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={() => void handleDeleteRuns()}
                                        disabled={selectedRunIdsForDelete.size === 0 || isDeletingRuns}
                                    >
                                        {isDeletingRuns ? "Deleting…" : "Delete"}
                                    </button>
                                    {runDeleteError && (
                                        <p className="mt-1 text-h5 text-warning" role="alert">
                                            {runDeleteError}
                                        </p>
                                    )}
                                </div>
                                </div>
                            )}
                        <div className="min-h-0 flex-1 overflow-y-auto p-3">
                            {runsError && (
                                <p className="text-h5 text-warning">{runsError}</p>
                            )}
                            <div className="space-y-2">
                                {runs.map((run) => (
                                    <div
                                        key={run.id}
                                        className={`relative w-full rounded-r5 border ${selectedRunId === run.id
                                            ? "border-primary2 bg-black80"
                                            : "border-black80 bg-black90 hover:border-black60"
                                            }`}
                                    >
                                        {isRunDeleteMode && (
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${run.name} log`}
                                                checked={selectedRunIdsForDelete.has(run.id)}
                                                onChange={() => toggleRunForDelete(run.id)}
                                                disabled={run.status === "running"}
                                                title={run.status === "running" ? "Running logs cannot be deleted" : undefined}
                                                className="absolute left-2 top-2 h-4 w-4 accent-primary2 disabled:cursor-not-allowed disabled:opacity-50"
                                            />
                                        )}
                                        <button
                                            type="button"
                                            className={`w-full rounded-r5 p-2 text-left ${isRunDeleteMode ? "pl-8" : ""}`}
                                            onClick={() => handleRunSelect(run.id)}
                                        >
                                            <p className="text-h5 font-semibold text-white">{run.name}</p>
                                            <p className="text-h5 text-black40">
                                                {run.startTime} ({run.status})
                                            </p>
                                            {run.arcSerial && (
                                                <p className="text-h5 text-black40">ARC {run.arcSerial}</p>
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {!isRunsLoading && runs.length === 0 && (
                                <p className="text-h5 text-black40">No runs available.</p>
                            )}
                        </div>
                    </aside>

                    <section className="flex min-h-0 flex-1 flex-col gap-3 rounded-r10 border border-black80 bg-black100 p-4">
                        {requestedRunPending && (
                            <div className="rounded-r5 border border-black70 bg-black90 p-3 text-h5 text-black40">
                                This run is outside the selected date range or is still syncing from the ARC. The run list and details will refresh automatically when it appears.
                            </div>
                        )}
                        <div className={`grid h-[clamp(14rem,38%,22rem)] shrink-0 gap-3 ${SHOW_MAP_REPLAY ? "grid-cols-2" : "grid-cols-1"}`}>
                            {SHOW_MAP_REPLAY && (
                                <div
                                    className="flex h-full min-h-0 w-full items-center justify-center overflow-hidden rounded-r10 border border-black80 bg-black90 px-3"
                                    aria-label="Map replay"
                                >
                                    <p className="text-center text-h5 text-black40">Map replay</p>
                                </div>
                            )}
                            <div className="flex h-full min-h-0 flex-col gap-2">
                                {fleetMode && selectedRun?.status === "incomplete" && (
                                    <div
                                        className="shrink-0 rounded-r5 border border-warning/40 bg-warning/10 px-3 py-2 text-center text-h5 text-warning"
                                        role="status"
                                    >
                                        This mission log is incomplete. Bring {selectedRun.arcSerial} online again to complete syncing
                                    </div>
                                )}
                                {fleetMode && selectedRecording?.fetchStatus === "failed" && (
                                    <div
                                        className="flex shrink-0 items-center justify-between gap-3 rounded-r5 border border-warning/60 bg-warning/10 px-3 py-2 text-h5 text-warning"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        <span>Recording import failed after repeated attempts.</span>
                                        <button
                                            type="button"
                                            className="shrink-0 rounded-r5 border border-warning px-2 py-1 text-h5 text-warning hover:bg-warning/20 disabled:cursor-not-allowed disabled:opacity-60"
                                            onClick={() => void handleRecordingRefetch()}
                                            disabled={isRefetchingRecording}
                                        >
                                            {isRefetchingRecording ? "Retrying…" : "Retry recording import"}
                                        </button>
                                    </div>
                                )}
                                {recordingRefetchMessage && (
                                    <p className="shrink-0 text-h5 text-black40" role="status" aria-live="polite">
                                        {recordingRefetchMessage}
                                    </p>
                                )}
                                <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-r10 border border-black80 bg-black90 p-3">
                                    {fleetMode ? (
                                        selectedRun?.arcSerial ? (
                                            <FleetRunRecording
                                                ref={videoRef}
                                                proxyHostname={fleetProxyHost}
                                                sourceArcSerial={selectedRun.arcSerial}
                                                sourceRunId={selectedRun.sourceRunId}
                                                recording={selectedRecording}
                                                metadataLoading={isRecordingMetadataLoading}
                                                className="aspect-video w-full max-h-full rounded-r10 bg-black100 object-contain"
                                            />
                                        ) : (
                                            <div className="flex aspect-video w-full max-h-full items-center justify-center rounded-r10 border border-black70 bg-black100 text-h5 text-black40">
                                                Video replay
                                            </div>
                                        )
                                    ) : selectedRun?.hasRecording ? (
                                        <video
                                            key={selectedRun.id}
                                            ref={videoRef}
                                            src={arcStorageUrl(`/runs/${selectedRun.sourceRunId}/recording`, selectedRun.arcSerial)}
                                            className="aspect-video w-full max-h-full rounded-r10 bg-black100 object-contain"
                                        />
                                    ) : (
                                        <div className="flex aspect-video w-full max-h-full items-center justify-center rounded-r10 border border-black70 bg-black100 text-h5 text-black40">
                                            {selectedRun ? "No recording" : "Video replay"}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="shrink-0 flex items-center gap-2">
                            <FunctionalButton
                                icon={isPlaying
                                    ? <span className="text-h5 font-semibold">II</span>
                                    : <PlayIcon className="h-4 w-4" />
                                }
                                className="h-8 w-8 rounded-r5 border border-primary2 bg-primary2 text-white disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={!selectedRun || !isTimelineComplete}
                                onClick={() => {
                                    if (isPlaying) {
                                        setIsPlaying(false);
                                        videoRef.current?.pause();
                                    } else {
                                        if (!selectedRun) return;
                                        const startMs = playheadMs >= selectedRunDurationMs ? 0 : playheadMs;
                                        if (startMs === 0) setPlayheadMs(0);
                                        if (videoRef.current) {
                                            videoRef.current.currentTime = startMs / 1000;
                                            videoRef.current.play().catch(() => { });
                                        }
                                        setIsPlaying(true);
                                    }
                                }}
                            />
                            <FunctionalButton
                                icon={<StopIcon className="h-4 w-4" />}
                                className="h-8 w-8 rounded-r5 border border-black70 bg-black90 text-black40 hover:text-white"
                                onClick={() => {
                                    setIsPlaying(false);
                                    setPlayheadMs(0);
                                    if (videoRef.current) {
                                        videoRef.current.pause();
                                        videoRef.current.currentTime = 0;
                                    }
                                }}
                            />
                            <div className="ml-1 flex flex-1 items-center gap-3">
                                <div className="relative h-8 flex-1 overflow-hidden rounded-r5 border border-black70 bg-black90 px-3">
                                    <div className="absolute inset-y-0 left-3 right-3 my-auto h-1 rounded-full bg-black70" />
                                    <div
                                        className="absolute inset-y-0 left-3 my-auto h-1 rounded-full bg-primary2"
                                        style={{ width: `calc((100% - 1.5rem) * ${progressRatio})` }}
                                    />
                                    {visibleMissionLogEvents.map((event) => (
                                        <div
                                            key={event.id}
                                            className="absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
                                            style={{
                                                left: `calc(0.75rem + (100% - 1.5rem) * ${event.offsetMs / selectedRunDurationMs
                                                    })`,
                                                opacity: event.offsetMs <= playheadMs ? 1 : 0.4,
                                            }}
                                        />
                                    ))}
                                    <div
                                        className="absolute inset-y-0 my-auto h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-primary2 bg-black100"
                                        style={{ left: `calc(0.75rem + (100% - 1.5rem) * ${progressRatio})` }}
                                    />
                                    <input
                                        type="range"
                                        min={0}
                                        max={selectedRunDurationMs}
                                        step={100}
                                        value={playheadMs}
                                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                        onChange={(e) => {
                                            const ms = Number(e.target.value);
                                            setIsPlaying(false);
                                            setPlayheadMs(ms);
                                            if (videoRef.current) {
                                                videoRef.current.pause();
                                                videoRef.current.currentTime = ms / 1000;
                                            }
                                        }}
                                        disabled={!selectedRun}
                                    />
                                </div>
                                <p className="shrink-0 text-h5 text-black40">
                                    {selectedRun
                                        ? `${formatElapsed(playheadMs)} / ${formatElapsed(selectedRunDurationMs)}`
                                        : "Timeline"}
                                </p>
                            </div>
                        </div>

                        <div className="min-h-0 flex flex-1 flex-col overflow-hidden rounded-r10 border border-black80 bg-black90">
                            {isMissionLogsTab && (
                                <div className="grid shrink-0 grid-cols-[7rem_8rem_1fr_4rem_1.5rem] border-b border-black80 px-3 py-2 text-h5 text-black40">
                                    <p>Time</p>
                                    <p>Type</p>
                                    <p>Detail</p>
                                    <p>Clip</p>
                                    <p />
                                </div>
                            )}
                            <div ref={timelineScrollRef} className="min-h-0 flex-1 overflow-y-auto">
                                {isMissionLogsTab && eventsError && (
                                    <p className="px-3 py-3 text-h5 text-warning">{eventsError}</p>
                                )}
                                {isMissionLogsTab && isEventsLoading && selectedRunEvents.length === 0 && (
                                    <p className="px-3 py-3 text-h5 text-black40">Loading events...</p>
                                )}
                                {isMissionLogsTab &&
                                    visibleMissionLogEvents.map((event) => {
                                        const isDetectionTopic = isDetectionResultData(event.data);
                                        const hasAttachments = event.attachments.length > 0;
                                        const isExpandable = isDetectionTopic || hasAttachments;
                                        const isExpanded = expandedEventIds.has(event.id);
                                        const isActive = event.offsetMs <= playheadMs;
                                        return (
                                            <div
                                                key={event.id}
                                                ref={(el) => {
                                                    if (el) eventRowRefs.current.set(event.id, el);
                                                    else eventRowRefs.current.delete(event.id);
                                                }}
                                                className="border-b border-black80 last:border-b-0"
                                            >
                                                <div
                                                    className={`grid grid-cols-[7rem_8rem_1fr_4rem_1.5rem] px-3 py-2 text-h5 cursor-pointer hover:bg-black80 ${isActive ? "text-white font-semibold" : "text-black40"
                                                        }`}
                                                    onClick={() => {
                                                        setIsPlaying(false);
                                                        setPlayheadMs(event.offsetMs);
                                                        if (videoRef.current) {
                                                            videoRef.current.pause();
                                                            videoRef.current.currentTime = event.offsetMs / 1000;
                                                        }
                                                    }}
                                                >
                                                    <p>{formatElapsed(event.offsetMs)}</p>
                                                    <p>{event.type}</p>
                                                    <p>
                                                        {event.detail}
                                                        {event.payload ? ` | ${event.payload}` : ""}
                                                    </p>
                                                    <div className="flex items-center gap-1">
                                                        {fleetMode && (() => {
                                                            // Import state of this row's attachments. Only
                                                            // meaningful in fleet mode: direct mode reads the
                                                            // ARC's own store, where there is no import step.
                                                            const state = attachmentImportState(event.attachments);
                                                            if (state === "uploading") {
                                                                return (
                                                                    <span title="Uploading to cloud" className="flex shrink-0">
                                                                        <SyncUploadingIcon className="w-3" />
                                                                    </span>
                                                                );
                                                            }
                                                            if (state === "failed") {
                                                                return (
                                                                    <span title="Not uploaded" className="flex shrink-0">
                                                                        <NotSyncIcon className="w-3" />
                                                                    </span>
                                                                );
                                                            }
                                                            // Fully uploaded rows show nothing: an icon on
                                                            // every row once the importer catches up is noise.
                                                            return null;
                                                        })()}
                                                        {(() => {
                                                            const pending = event.clipJobs.some(
                                                                (j) => j.status === "pending" || j.status === "running"
                                                            );
                                                            const done = event.clipJobs.find(
                                                                (j) => j.status === "completed" && j.attachmentId
                                                            );
                                                            if (done) {
                                                                return (
                                                                    <a
                                                                        href={arcStorageUrl(`/attachments/${done.attachmentId}/view`)}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-h5 text-primary2 hover:underline"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        clip
                                                                    </a>
                                                                );
                                                            }
                                                            if (pending) {
                                                                return (
                                                                    <span className="text-h5 text-black40 italic">
                                                                        pending
                                                                    </span>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                    </div>
                                                    <div
                                                        className="flex items-center justify-center"
                                                        onClick={isExpandable ? (e) => { e.stopPropagation(); toggleExpanded(event.id); } : undefined}
                                                    >
                                                        {isExpandable && (
                                                            <span className="inline-block cursor-pointer text-h5 text-black40">
                                                                {isExpanded ? "▼" : "▲"}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {isExpanded && isDetectionTopic && (
                                                    <DetectionPanel
                                                        hits={filterDetectionHitsByCriteria(
                                                            event.detectionHits ??
                                                                extractDetectionHits(event.data),
                                                            topicRuleCriteria[event.type] ?? null
                                                        )}
                                                        attachments={event.attachments}
                                                        onImageClick={setLightboxAttachment}
                                                        AttachmentImage={AttachmentImage}
                                                        AttachmentVideo={AttachmentVideo}
                                                    />
                                                )}
                                                {isExpanded && !isDetectionTopic && hasAttachments && (
                                                    <div className="flex gap-2 overflow-x-auto bg-black80 px-3 py-2">
                                                        {event.attachments.filter((a) => a.mimeType.startsWith("image/")).map((att) => (
                                                            <button
                                                                key={att.id}
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setLightboxAttachment(att);
                                                                }}
                                                                className="h-32 w-48 shrink-0 overflow-hidden rounded-r5 border border-black70 hover:border-primary2"
                                                            >
                                                                <AttachmentImage
                                                                    attachment={att}
                                                                    className="h-full w-full object-cover"
                                                                />
                                                            </button>
                                                        ))}
                                                        {event.attachments.filter((a) => a.mimeType.startsWith("video/")).map((att) => (
                                                            <AttachmentVideo
                                                                key={att.id}
                                                                attachment={att}
                                                                className="h-32 w-48 shrink-0 rounded-r5"
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                {isMissionLogsTab && !isEventsLoading && !selectedRun && (
                                    <p className="px-3 py-3 text-h5 text-black40">
                                        No run selected.
                                    </p>
                                )}
                                {!isMissionLogsTab && (
                                    allImages.length === 0 ? (
                                        <p className="px-3 py-3 text-h5 text-black40">
                                            {isEventsLoading ? "Loading..." : "No images in this run."}
                                        </p>
                                    ) : (
                                        <div className="grid grid-cols-3 gap-2 overflow-y-auto p-3">
                                            {allImages.map((att) => (
                                                <button
                                                    key={att.id}
                                                    type="button"
                                                    className="group relative overflow-hidden rounded-r5 border border-black70 hover:border-primary2"
                                                    onClick={() => setLightboxAttachment(att)}
                                                >
                                                    <AttachmentImage
                                                        attachment={att}
                                                        className="h-24 w-full object-cover"
                                                    />
                                                    <p className="absolute bottom-0 w-full truncate bg-black bg-opacity-60 px-1 text-h5 text-white opacity-0 transition-opacity group-hover:opacity-100">
                                                        {att.fileName}
                                                    </p>
                                                </button>
                                            ))}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {lightboxAttachment && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
                    onClick={() => setLightboxAttachment(null)}
                >
                    <div
                        className="max-h-[90vh] max-w-[90vw] rounded-r10 bg-black100 p-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <AttachmentImage
                            attachment={lightboxAttachment}
                            className="max-h-[85vh] max-w-[85vw] rounded object-contain"
                        />
                        <p className="mt-1 text-center text-h5 text-black40">
                            {lightboxAttachment.fileName}
                            {lightboxAttachment.size != null
                                ? ` — ${(lightboxAttachment.size / 1024).toFixed(1)} KB`
                                : ""}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ControlLogsPage;
